/*
 * Copyright [ 2020 - 2024 ] [Matthew Buckton]
 * Copyright [ 2024 - 2025 ] [Maps Messaging B.V.]
 *
 *  Licensed under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License.
 *  You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

"use client";

import {ReactNode, useEffect, useState} from "react";
import {useRouter} from "next/router"; // ✅ Use Next.js Router
import {
    AppBar,
    Avatar,
    Box,
    CircularProgress,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Toolbar,
    Typography
} from "@mui/material";
import {useAuth0} from "@auth0/auth0-react";
import LogoutButton from "./auth/logout-button";
import { useCallback } from "react";

const drawerWidth = 240;



const menuItems = [
    { name: "Clients", path: "/dashboard/client" },
    { name: "Licenses", path: "/dashboard/licenses" },
    { name: "Config", path: "/dashboard/config" }
];


const Layout = ({ children }: { children: ReactNode }) => {
    const { user, isAuthenticated, isLoading } = useAuth0();
    const [mounted, setMounted] = useState(false);
    const router = useRouter();
    const { loginWithRedirect } = useAuth0();

    const getInitialMenu = useCallback(() => {
        const found = menuItems.find((item) => router.pathname.startsWith(item.path));
        return found ? found.name : "Clients"; // Default to "Clients"
    }, [router.pathname]);

    useEffect(() => {
        setSelectedMenu(getInitialMenu());
    }, [getInitialMenu]);

    const [selectedMenu, setSelectedMenu] = useState<string>(getInitialMenu);

    const [versionInfo, setVersionInfo] = useState<{ version: string, name: string, date: string } | null>(null);

    useEffect(() => {
        fetch("/version.json")
            .then((res) => res.json())
            .then((data) => setVersionInfo(data))
            .catch(() => setVersionInfo(null));
    }, []);

    useEffect(() => {
        setMounted(true);
        if (!isLoading && !isAuthenticated) {
            loginWithRedirect();
        }
    }, [isLoading, isAuthenticated, router]);

    if (!mounted || isLoading) return <CircularProgress />; // Prevents hydration issues
    if (!isAuthenticated) return null; // Prevents unauthorized rendering

    return (
        <Box sx={{ display: "flex" }}>
            {/* ✅ Sidebar (Drawer) */}
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box" },
                }}
            >
                <Box sx={{ p: 2, display: "flex", alignItems: "center", flexDirection: "column" }}>
                    <Avatar sx={{ width: 56, height: 56, mb: 1 }}>
                        {user ? user.nickname?.charAt(0).toUpperCase() : "U"}
                    </Avatar>
                    <Typography variant="subtitle1">{user ? user.nickname : "Guest"}</Typography>
                    <LogoutButton />
                </Box>
                <List>
                    {menuItems.map((item) => (
                        <ListItem key={item.path} disablePadding>
                            <ListItemButton
                                selected={selectedMenu === item.name} // ✅ Correctly highlights the selected menu
                                onClick={() => {
                                    setSelectedMenu(item.name);
                                    router.push(item.path);
                                }}
                            >
                                <ListItemText primary={item.name} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Drawer>

            {/* ✅ Main Content Area */}
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                <AppBar position="fixed" sx={{ width: `calc(100% - ${drawerWidth}px)`, ml: `${drawerWidth}px` }}>
                    <Toolbar>
                        <Typography variant="h6" noWrap>
                            Maps Messaging License Manager
                            {versionInfo && ` v${versionInfo.version} – ${versionInfo.name}`}
                        </Typography>
                    </Toolbar>
                </AppBar>

                {/* ✅ Content Wrapper - Uses Next.js Routing to Load Pages */}
                <Box sx={{ flexGrow: 1, p: 3, mt: 8 }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
};

export default Layout;
